import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

export const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHomePage = location.pathname === '/';
  const navBg = isScrolled || !isHomePage
    ? 'bg-white/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';
  const textColor = isScrolled || !isHomePage ? 'text-[#0F1F3C]' : 'text-white';

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/rooms', label: t('nav.rooms') },
    { path: '/experiences', label: t('nav.experiences') },
    { path: '/gallery', label: t('nav.gallery') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className={`font-serif text-2xl tracking-tight ${textColor} transition-colors duration-300`}
            data-testid="logo-link"
          >
            Le Château d'Azur
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs uppercase tracking-[0.2em] font-sans font-medium ${textColor} hover:text-[#D4AF37] transition-colors duration-300`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`flex items-center space-x-2 ${textColor} hover:text-[#D4AF37] transition-colors`}
                data-testid="language-selector"
              >
                <Globe className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider font-medium">
                  {language.toUpperCase()}
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-none shadow-xl">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-[#0F1F3C]/5' : ''}`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book Now Button */}
            <Link
              to="/rooms"
              className="bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] px-8 py-3 text-xs uppercase tracking-widest font-medium transition-all duration-300"
              data-testid="book-now-btn"
            >
              {t('nav.bookNow')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden ${textColor}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 space-y-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-[#0F1F3C] text-sm uppercase tracking-wider font-medium"
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-6 border-t border-gray-100 flex items-center space-x-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`px-3 py-2 text-xs uppercase ${
                      language === lang.code
                        ? 'bg-[#0F1F3C] text-white'
                        : 'bg-gray-100 text-[#0F1F3C]'
                    }`}
                  >
                    {lang.code}
                  </button>
                ))}
              </div>
              <Link
                to="/rooms"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block bg-[#0F1F3C] text-white text-center py-4 text-xs uppercase tracking-widest font-medium"
              >
                {t('nav.bookNow')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
